import { determineIdentityProviderById, IdentityProviders } from "@courses-models/user/src/ts/utils";

/**
 * Collection of different available user types, so that we can easily determine
 * what parts of the interface a given user should be granted access to.
 */
export enum UserTypes {
    STUDENT,
    TEACHER,
}

/**
 * Abstract specification of how user data is being presented to frontends,
 * making the interface similar between both E-key and Uni-C users.
 */
export abstract class UserModel {
    /**
     * Specifies the id of the user, which can be used when interacting with
     * most of Gyldendals endpoints.
     */
    public readonly id: string;

    /**
     * Specifies the user's e-mail-address
     */
    public readonly mail: string | null;

    /**
     * Specifies the user's name
     */
    public readonly name: string;

    /**
     * Apply data initially when creating a user model.
     */
    constructor(data: {
        id: string;
        mail: string | null;
        name: string;
    }) {
        this.id = data.id;
        this.mail = data.mail;
        this.name = data.name;
    }

    /**
     * Helper that'll return either an e-key or a unic user model based on the
     * given id.
     */
    public static async GET_BY_ID(id: string): Promise<UserModel> {

        switch (determineIdentityProviderById(id)) {
            case IdentityProviders.EKEY:
                const { EkeyUserModel } = await import("@courses-models/user/src/ts/models/EkeyUserModel");

                return EkeyUserModel.GET_BY_ID(id);

            case IdentityProviders.UNIC:
                const { UnicUserModel } = await import("@courses-models/user/src/ts/models/UnicUserModel");

                return UnicUserModel.GET_BY_ID(id);

            default:
                throw new Error("UserModel.GET_BY_ID(...): Unable to determine identity provider.");
        }
    }

    /**
     * Helper that'll resolve the class that this user is a part of (this
     * applies only to students!).
     */
    public abstract getClass(): Promise<string | null>;

    /**
     * Helper that'll resolve the institution id associated with the user, when
     * he's accessing the given product.
     */
    public abstract getInstitutionId(productId?: string): Promise<string | null>;

    /**
     * Helper that'll look up the user's type from a remote endpoint. The
     * product id is only relevant for E-key users.
     */
    public abstract getUserType(productId?: string): Promise<UserTypes | null>;
}
